import React from "react";
import { AbsoluteFill, interpolate, spring, useCurrentFrame, useVideoConfig } from "remotion";
import { COLORS, FONTS, SPRING } from "./constants";
import { Backdrop, glow } from "./shared";

const DEBRIS = [
  { x: 220, y: 260, w: 180, h: 120, rot: -8, label: "contract.pdf", c: COLORS.neonRed },
  { x: 1500, y: 200, w: 200, h: 140, rot: 6, label: "pricing_v7.xlsx", c: COLORS.neonOrange },
  { x: 320, y: 700, w: 190, h: 130, rot: 4, label: "onboarding.md", c: COLORS.neonYellow },
  { x: 1450, y: 760, w: 170, h: 110, rot: -5, label: "q3-deck.pptx", c: COLORS.neonPink },
  { x: 880, y: 180, w: 160, h: 110, rot: 3, label: "slack-dump.txt", c: COLORS.neonBlue },
  { x: 780, y: 820, w: 180, h: 120, rot: -3, label: "handover.doc", c: COLORS.neonPurple },
  { x: 1100, y: 500, w: 150, h: 100, rot: 7, label: "call-notes.md", c: COLORS.neonCyan },
];

export const Scene01ColdOpen: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps, durationInFrames } = useVideoConfig();

  const fade = interpolate(frame, [durationInFrames - 30, durationInFrames], [1, 0], {
    extrapolateLeft: "clamp", extrapolateRight: "clamp",
  });

  const headlineAppear = spring({ frame: frame - 20, fps, config: SPRING.gentle });
  const subAppear = spring({ frame: frame - 120, fps, config: SPRING.gentle });

  return (
    <AbsoluteFill style={{ background: COLORS.bg, opacity: fade, overflow: "hidden" }}>
      <Backdrop frame={frame} accent={COLORS.neonRed} />

      {/* Danger red pulse overlay */}
      <div style={{
        position: "absolute", inset: 0,
        background: `radial-gradient(circle at 50% 55%, ${COLORS.neonRed}${Math.round((0.05 + Math.sin(frame * 0.05) * 0.03) * 255).toString(16).padStart(2, "0")} 0%, transparent 60%)`,
        pointerEvents: "none",
      }} />

      {/* Floating 3D document debris */}
      <div style={{ position: "absolute", inset: 0, perspective: 1400 }}>
        {DEBRIS.map((d, i) => {
          const apSp = spring({ frame: frame - i * 6, fps, config: SPRING.slow });
          const op = interpolate(apSp, [0, 1], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
          const sc = interpolate(apSp, [0, 1], [0.5, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
          const driftY = Math.sin(frame * 0.015 + i) * 14;
          const driftRotZ = Math.sin(frame * 0.012 + i * 0.8) * 3;
          return (
            <div key={i} style={{
              position: "absolute",
              left: d.x, top: d.y + driftY,
              width: d.w, height: d.h,
              transform: `perspective(1000px) rotateX(${Math.sin(frame * 0.01 + i) * 6}deg) rotateY(${d.rot + Math.cos(frame * 0.008 + i) * 4}deg) rotateZ(${driftRotZ}deg) scale(${sc})`,
              opacity: op,
              transformStyle: "preserve-3d",
            }}>
              <div style={{
                width: "100%", height: "100%",
                background: `linear-gradient(135deg, ${COLORS.bgCardLight}, ${COLORS.bgCard})`,
                border: `1.5px solid ${d.c}80`,
                borderRadius: 10,
                boxShadow: `0 0 30px ${d.c}55, 0 12px 30px rgba(0,0,0,0.6)`,
                padding: "12px 14px",
                display: "flex", flexDirection: "column",
              }}>
                <div style={{
                  fontSize: 12, fontFamily: FONTS.mono, color: d.c,
                  borderBottom: `1px solid ${d.c}40`, paddingBottom: 6, marginBottom: 8,
                  textShadow: glow(d.c),
                }}>{d.label}</div>
                {[...Array(4)].map((_, k) => (
                  <div key={k} style={{
                    height: 3, background: `${COLORS.textMuted}55`, marginBottom: 5,
                    width: `${90 - k * 12}%`, borderRadius: 2,
                  }} />
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* Headline text */}
      <div style={{
        position: "absolute", left: 0, right: 0, top: 420,
        textAlign: "center", opacity: headlineAppear,
        transform: `translateY(${(1 - headlineAppear) * 20}px)`,
      }}>
        <div style={{
          fontSize: 76,
          fontFamily: FONTS.heading,
          fontWeight: 900,
          color: COLORS.textPrimary,
          textShadow: `0 0 40px ${COLORS.neonRed}aa, 0 4px 30px rgba(0,0,0,0.9)`,
          letterSpacing: -2,
          lineHeight: 1.05,
        }}>
          Every business is drowning<br />in its own knowledge.
        </div>
        <div style={{
          fontSize: 28,
          fontFamily: FONTS.body,
          color: COLORS.neonRed,
          marginTop: 28,
          opacity: subAppear,
          transform: `translateY(${(1 - subAppear) * 10}px)`,
          textShadow: glow(COLORS.neonRed),
          fontWeight: 600,
          letterSpacing: 1.5,
        }}>
          Scattered. Lost. Walking out the door.
        </div>
      </div>
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
